import React from 'react';
import {Container, Row, Col, Card, Badge, Button} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faChartLine, faClock, faPoundSign,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faChartBar, faRandom, faDatabase
} from '@fortawesome/free-solid-svg-icons';
import {Link} from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from '../../assets/images/use-cases/Order-mgt-dianosis-pyramid.png'
import vis from '../../assets/images/use-cases/Order-mgt-dianosis-dashboard.png'

const LateOrdersDiagnosis = () => {
    return (
        <>
            <SEO
                title="Late Orders Diagnosis: 50% → 90% On-Time | Nebulyx AI"
                description="How we can diagnose late orders using AI Digital Twins, recovering £1.2M annually."
                keywords="AI digital twin, late orders, supply chain, inventory bottleneck"
                canonicalUrl="https://nebulyx.ai/use-cases/late-orders-diagnosis"
                ogType="article"
            />

            <article className="use-case-page">
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4">
                                            <FontAwesomeIcon icon={faShoppingCart} className=""/>
                                            Retail
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;Why are my orders dropping by 50%?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model the order and inventory flows to discover why the order rate has dropped.                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">50% → 90%</span>
                                <span className="stat-label">On-Time Delivery</span>
                            </Col>
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faPoundSign} className="stat-icon"/>
                                <span className="stat-value">£1.2M</span>
                                <span className="stat-label">Annual Recovery</span>
                            </Col>
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">Time Saved</span>
                                <span className="stat-label">Days, not months</span>
                            </Col>
                        </Row>
                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>50% of orders delivered late</li>
                                            <li>£1.5M annual loss from delays</li>
                                            <li>Dashboards showed <em>what</em> but not <em>why</em></li>
                                            <li>Root cause invisible to analytics</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Built AI Digital Twin targeting "50% late"</li>
                                            <li>Simulated full order-to-delivery process</li>
                                            <li>Tested hypotheses with right tools</li>
                                            <li>Found hidden inventory bottleneck</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>90% on-time delivery achieved</li>
                                            <li>£1.2M annual savings recovered</li>
                                            <li>Completed in hours, not months</li>
                                            <li>Reusable inventory optimisation tool</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Capture Signals</strong>
                                    <span>Order fill rates, customer abandonment, on-time deliveries, click-stream data</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Analyse & Map</strong>
                                    <span>Modelled customer booking and inventory flows with predictive analytics</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Test Hypotheses</strong>
                                    <span>Tested buying patterns and inventory capability scenarios</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Outcome</strong>
                                    <span>Found warehouse inventories weren't aligned, causing late/cancelled orders</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>

                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests order, inventory, fulfilment and delivery data to detect
                                            bottlenecks, correlate stock with lateness, and simulate what-if scenarios
                                            that output stockout alerts and recommended actions.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Order Optimisation Dashboard</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Displays current vs projected late rates, allows adjustment of reorder
                                            point, picking capacity and carrier mix levers, visualises root cause
                                            attribution, and surfaces the recommended action with confidence
                                            scoring. </p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Customer Orders</li>
                                            <li>Inventory from each warehouse</li>
                                            <li>Orders fullment data</li>
                                            <li>Order to customer Delivery data</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI and Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Order Records</li>
                                            <li>Inventory Levels</li>
                                            <li>Fulfilment Events</li>
                                            <li>Delivery Tracking</li>
                                            <li>Enriched Timeline</li>
                                            <li>Bottleneck Detection</li>
                                            <li>Stock-Late Correlation</li>
                                            <li>Demand Forecast</li>
                                            <li>Root Cause Model</li>
                                            <li>What-If Simulator</li>
                                            <li>Stockout Alerts</li>
                                            <li>Recommended Action</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Increase Reorder Point</li>
                                            <li>Add Picking Capacity</li>
                                            <li>Change Carrier Mix</li>
                                            <li>Adjust Safety Stock</li>
                                            <li>Reallocate Warehouse Load</li>
                                            <li>Extend Shift Hours</li>
                                            <li>Fast-Track Priority Orders</li>
                                            <li>Split Fulfilment Routes</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By simulating the full process as a <strong>Data Object Graph</strong>, an
                                        <strong> inventory process bottleneck</strong> can be surfaced that is
                                        completely invisible to
                                        correlation analysis. This surfaced the 2nd and 3rd order causes.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Root Cause Found</span>
                                        <span
                                            className="discovery-value">Inventory Bottleneck 50% orders cancelled</span>
                                        <span className="discovery-note">Inventory optimisation delayed orders from 50 to 10% = 1.2M saved</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Facing a Similar Challenge?</h2>
                            <p>
                                If your dashboards show <em>what</em> but can't explain <em>why</em>,
                                let's diagnose your business problem together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default LateOrdersDiagnosis;